import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface BundleEffectivenessScore {
  bundle_id: string;
  bundle_name: string;
  bundle_price: number;
  effectiveness_score: number;
  conversion_score: number;
  revenue_score: number;
  usage_score: number;
  total_deals: number;
  won_deals: number;
  conversion_rate: number;
  total_revenue: number;
  usage_count: number;
  avg_deal_size: number;
  deal_size_range: { min: number; max: number };
}

export function useBundleEffectiveness() {
  return useQuery({
    queryKey: ['bundle-effectiveness'],
    queryFn: async () => {
      // Get all line items with bundle_id and their deal data
      const { data: lineItems, error: lineItemsError } = await supabase
        .from('deal_line_items')
        .select(`
          bundle_id,
          total_price,
          deal:deals(id, status, amount)
        `)
        .not('bundle_id', 'is', null);

      if (lineItemsError) throw lineItemsError;

      // Get all bundles
      const { data: bundles, error: bundlesError } = await supabase
        .from('product_bundles')
        .select('id, name, bundle_price, is_active');

      if (bundlesError) throw bundlesError;

      const bundleMap = new Map(bundles?.map(b => [b.id, b]) || []);

      // Aggregate stats by bundle
      const statsMap = new Map<string, {
        bundle_id: string;
        deal_ids: Set<string>;
        won_deal_ids: Set<string>;
        total_revenue: number;
        usage_count: number;
        deal_amounts: number[];
      }>();

      lineItems?.forEach((item: any) => {
        if (!item.bundle_id || !item.deal) return;

        const dealId = item.deal.id;
        const dealStatus = item.deal.status;
        const dealAmount = item.deal.amount || 0;

        const existing = statsMap.get(item.bundle_id);

        if (existing) {
          existing.usage_count += 1;
          if (!existing.deal_ids.has(dealId)) {
            existing.deal_ids.add(dealId);
            existing.deal_amounts.push(dealAmount);
            if (dealStatus === 'won') {
              existing.won_deal_ids.add(dealId);
              existing.total_revenue += dealAmount;
            }
          }
        } else {
          const newEntry = {
            bundle_id: item.bundle_id,
            deal_ids: new Set<string>([dealId]),
            won_deal_ids: new Set<string>(),
            total_revenue: 0,
            usage_count: 1,
            deal_amounts: [dealAmount],
          };
          
          if (dealStatus === 'won') {
            newEntry.won_deal_ids.add(dealId);
            newEntry.total_revenue = dealAmount;
          }
          
          statsMap.set(item.bundle_id, newEntry);
        }
      });

      // Calculate effectiveness scores
      const allStats = Array.from(statsMap.values());
      
      // Find max values for normalization
      const maxConversion = Math.max(...allStats.map(s => {
        const closed = s.deal_ids.size - [...s.deal_ids].filter(id => {
          const item = lineItems?.find((li: any) => li.deal?.id === id);
          return item?.deal?.status === 'open';
        }).length;
        return closed > 0 ? s.won_deal_ids.size / closed : 0;
      }), 0.01);
      
      const maxRevenue = Math.max(...allStats.map(s => s.total_revenue), 1);
      const maxUsage = Math.max(...allStats.map(s => s.usage_count), 1);

      const scores: BundleEffectivenessScore[] = allStats.map(s => {
        const bundle = bundleMap.get(s.bundle_id);
        const totalDeals = s.deal_ids.size;
        const wonDeals = s.won_deal_ids.size;
        
        // Calculate closed deals (won + lost, excluding open)
        const openDeals = [...s.deal_ids].filter(id => {
          const item = lineItems?.find((li: any) => li.deal?.id === id);
          return item?.deal?.status === 'open';
        }).length;
        const closedDeals = totalDeals - openDeals;
        
        const conversionRate = closedDeals > 0 ? (wonDeals / closedDeals) * 100 : 0;
        
        // Normalize scores (0-100)
        const conversionScore = closedDeals > 0 ? ((wonDeals / closedDeals) / maxConversion) * 100 : 0;
        const revenueScore = (s.total_revenue / maxRevenue) * 100;
        const usageScore = (s.usage_count / maxUsage) * 100;
        
        // Combined effectiveness score (weighted average)
        // Conversion: 50%, Revenue: 30%, Usage: 20%
        const effectivenessScore = (conversionScore * 0.5) + (revenueScore * 0.3) + (usageScore * 0.2);
        
        const avgDealSize = s.deal_amounts.length > 0 
          ? s.deal_amounts.reduce((a, b) => a + b, 0) / s.deal_amounts.length 
          : 0;

        return {
          bundle_id: s.bundle_id,
          bundle_name: bundle?.name || 'Unknown Bundle',
          bundle_price: bundle?.bundle_price || 0,
          effectiveness_score: Math.round(effectivenessScore),
          conversion_score: Math.round(conversionScore),
          revenue_score: Math.round(revenueScore),
          usage_score: Math.round(usageScore),
          total_deals: totalDeals,
          won_deals: wonDeals,
          conversion_rate: conversionRate,
          total_revenue: s.total_revenue,
          usage_count: s.usage_count,
          avg_deal_size: avgDealSize,
          deal_size_range: {
            min: Math.min(...s.deal_amounts, 0),
            max: Math.max(...s.deal_amounts, 0),
          },
        };
      });

      return scores.sort((a, b) => b.effectiveness_score - a.effectiveness_score);
    },
  });
}

export function useBundleSuggestionsForDeal(dealAmount: number) {
  const { data: effectiveness } = useBundleEffectiveness();
  
  return useQuery({
    queryKey: ['bundle-suggestions-deal', dealAmount, effectiveness],
    queryFn: async () => {
      if (!effectiveness || effectiveness.length === 0) return [];

      // Find bundles that have been used in similar deal sizes
      const tolerance = 0.5; // 50% tolerance for deal size matching
      const minAmount = dealAmount * (1 - tolerance);
      const maxAmount = dealAmount * (1 + tolerance);

      const relevantBundles = effectiveness.filter(b => {
        // Check if bundle's deal size range overlaps with target
        return (b.deal_size_range.min <= maxAmount && b.deal_size_range.max >= minAmount) ||
               (b.bundle_price <= dealAmount * 0.8); // Or if bundle price is reasonable for deal
      });

      // Sort by effectiveness score but boost bundles closer to deal size
      const scoredBundles = relevantBundles.map(b => {
        const sizeFit = b.avg_deal_size > 0 
          ? 1 - Math.min(Math.abs(b.avg_deal_size - dealAmount) / dealAmount, 1)
          : 0.5;
        
        const adjustedScore = b.effectiveness_score * (0.7 + sizeFit * 0.3);
        
        return {
          ...b,
          adjusted_score: adjustedScore,
          size_fit_percent: Math.round(sizeFit * 100),
        };
      });

      return scoredBundles
        .sort((a, b) => b.adjusted_score - a.adjusted_score)
        .slice(0, 5);
    },
    enabled: !!effectiveness && dealAmount > 0,
  });
}
